/**
 * Email Ad Validation API Hook
 * Provides functions to interact with the Email Ad Validation API endpoints
 */
import { useState, useCallback } from 'react';
import api from '../../../services/api';
import {
  EMAIL_AD_VALIDATION_RUNS,
  EMAIL_AD_VALIDATION_RERUN,
  EMAIL_AD_VALIDATION_DELETE,
} from '../../../constants/api';

export const useEmailAdValidationApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Submit new validation run
  const submitValidationRun = useCallback(async (formData) => {
    try {
      setLoading(true);
      setError(null);

      // Format dates to YYYY-MM-DD
      const formatDateToYYYYMMDD = (date) => {
        if (!date) return null;
        const d = new Date(date);
        return d.toISOString().split('T')[0]; // Gets YYYY-MM-DD format
      };

      const payload = {
        run_name: formData.runName,
        date_from: formatDateToYYYYMMDD(formData.startDate),
        date_to: formatDateToYYYYMMDD(formData.endDate),
        subject: formData.subject,
        email_from: formData.fromEmail,
        email_to: formData.toEmail,
        email_type: formData.emailType,
        offer_matrix_url: formData.offerMatrix,
      };

      const response = await api.post(EMAIL_AD_VALIDATION_RUNS, payload);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit validation run');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch historical runs
  const fetchHistoricalRuns = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.emailType) params.append('email_type', filters.emailType);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.page) params.append('page', filters.page);

      const url = params.toString()
        ? `${EMAIL_AD_VALIDATION_RUNS}?${params}`
        : EMAIL_AD_VALIDATION_RUNS;

      const response = await api.get(url);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch historical runs');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array to prevent re-creation

  // Rerun an existing validation run
  const RerunValidationRun = useCallback(async (runId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post(EMAIL_AD_VALIDATION_RERUN(runId));
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to rerun validation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a validation run
  const deleteValidationRun = useCallback(async (runId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.delete(EMAIL_AD_VALIDATION_DELETE(runId));
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete validation run');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    clearError,
    submitValidationRun,
    fetchHistoricalRuns,
    RerunValidationRun,
    deleteValidationRun,
  };
};

export default useEmailAdValidationApi;
